// src/pages/Departments.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { Building2, Plus, Search, Edit2, Trash2, Power, Users, Database } from 'lucide-react';
import { Department, User } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';
import { useToast } from '../contexts/ToastContext';
import { apiRequest } from '../lib/api';
import { formatDateOnly } from '../lib/date';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Badge } from '../components/ui/Badge';
import { Modal } from '../components/ui/Modal';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';
import { TableSkeleton } from '../components/ui/TableSkeleton';
import { EmptyState } from '../components/ui/EmptyState';

export const Departments: React.FC = () => {
  const { can } = useAuth();
  const { settings } = useSettings();
  const { success, error: toastError } = useToast();

  const [departments, setDepartments] = useState<Department[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  // Create / Edit modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingDept, setEditingDept] = useState<Department | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    manager_user_id: '',
    status: 'active',
  });
  const [formSubmitting, setFormSubmitting] = useState(false);

  // Deletion / Deactivation dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    dept: Department | null;
    loading: boolean;
  }>({ open: false, dept: null, loading: false });

  const loadDepartments = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);

      const data = await apiRequest<Department[]>(`/api/departments?${params.toString()}`);
      setDepartments(data || []);
    } catch (err: any) {
      toastError(err.message || 'Failed to fetch departments.');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, toastError]);

  // Load active users for manager assignment
  useEffect(() => {
    const loadUsers = async () => {
      try {
        if (can('users.view')) {
          const res = await apiRequest<{ items: User[] }>('/api/users?pageSize=100');
          setUsers(res.items || []);
        }
      } catch {
        // non-blocking
      }
    };
    loadUsers();
  }, [can]);

  useEffect(() => {
    const timer = setTimeout(() => {
      loadDepartments();
    }, 200);
    return () => clearTimeout(timer);
  }, [loadDepartments]);

  const handleOpenCreate = () => {
    setEditingDept(null);
    setFormData({
      name: '',
      code: '',
      description: '',
      manager_user_id: '',
      status: 'active',
    });
    setModalOpen(true);
  };

  const handleOpenEdit = (dept: Department) => {
    setEditingDept(dept);
    setFormData({
      name: dept.name,
      code: dept.code,
      description: dept.description || '',
      manager_user_id: dept.manager_user_id ? String(dept.manager_user_id) : '',
      status: dept.status,
    });
    setModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.code) {
      toastError('Department name and code are required.');
      return;
    }

    setFormSubmitting(true);
    try {
      const payload = {
        name: formData.name,
        code: formData.code.toUpperCase(),
        description: formData.description,
        manager_user_id: formData.manager_user_id ? Number(formData.manager_user_id) : null,
        status: formData.status,
      };

      if (editingDept) {
        await apiRequest(`/api/departments/${editingDept.id}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
        success(`Department "${formData.name}" updated successfully.`);
      } else {
        await apiRequest('/api/departments', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        success(`Department "${formData.name}" created successfully.`);
      }

      setModalOpen(false);
      loadDepartments();
    } catch (err: any) {
      toastError(err.message || 'Failed to save department.');
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!confirmDialog.dept) return;

    setConfirmDialog(prev => ({ ...prev, loading: true }));
    try {
      const res = await apiRequest<{ message: string; deactivated?: boolean }>(
        `/api/departments/${confirmDialog.dept.id}`,
        { method: 'DELETE' }
      );
      success(res.message);
      setConfirmDialog({ open: false, dept: null, loading: false });
      loadDepartments();
    } catch (err: any) {
      toastError(err.message || 'Failed to delete department.');
      setConfirmDialog(prev => ({ ...prev, loading: false }));
    }
  };

  const handleToggleStatus = async (dept: Department) => {
    const nextStatus = dept.status === 'active' ? 'inactive' : 'active';
    try {
      await apiRequest(`/api/departments/${dept.id}`, {
        method: 'PUT',
        body: JSON.stringify({ status: nextStatus }),
      });
      success(`Department marked as ${nextStatus}.`);
      loadDepartments();
    } catch (err: any) {
      toastError(err.message || 'Could not change department status.');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header & Actions */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-slate-900">Departments</h2>
          <p className="text-xs text-slate-500">
            Define organizational boundaries, department codes, and unit managers
          </p>
        </div>
        {can('departments.create') && (
          <Button onClick={handleOpenCreate} icon={<Plus className="w-4 h-4" />}>
            Create Department
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <Input
            placeholder="Search by department name, code, or description..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            icon={<Search className="w-4 h-4" />}
          />
        </div>
        <div className="w-full sm:w-48">
          <Select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            options={[
              { value: '', label: 'All Statuses' },
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Inactive' },
            ]}
          />
        </div>
      </div>

      {/* Table Card */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <TableSkeleton rows={6} columns={6} />
        ) : departments.length === 0 ? (
          <EmptyState
            icon={<Building2 className="w-10 h-10 text-slate-400" />}
            title="No departments found"
            description="Create your company's departments to organize users and scope data access."
            actionLabel={can('departments.create') ? 'Create Department' : undefined}
            onAction={handleOpenCreate}
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-slate-600">
              <thead className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-3.5">Department</th>
                  <th className="px-6 py-3.5">Code</th>
                  <th className="px-6 py-3.5">Manager</th>
                  <th className="px-6 py-3.5">Assigned Scope</th>
                  <th className="px-6 py-3.5">Status</th>
                  <th className="px-6 py-3.5">Created</th>
                  <th className="px-6 py-3.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {departments.map(dept => (
                  <tr key={dept.id} className="hover:bg-slate-50/70 transition">
                    <td className="px-6 py-4">
                      <div className="font-semibold text-slate-900">{dept.name}</div>
                      {dept.description && (
                        <div className="text-xs text-slate-500 truncate max-w-xs">{dept.description}</div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-mono text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-200 font-semibold">
                        {dept.code}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-xs">
                      {dept.manager_name ? (
                        <span className="font-medium text-slate-800">{dept.manager_name}</span>
                      ) : (
                        <span className="text-slate-400 italic">Unassigned</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-xs text-slate-600">
                      <div className="flex items-center gap-3">
                        <span className="inline-flex items-center gap-1">
                          <Users className="w-3.5 h-3.5 text-slate-400" />
                          <strong>{dept.user_count || 0}</strong> users
                        </span>
                        <span className="inline-flex items-center gap-1">
                          <Database className="w-3.5 h-3.5 text-slate-400" />
                          <strong>{dept.record_count || 0}</strong> records
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <Badge status={dept.status} />
                    </td>
                    <td className="px-6 py-4 text-xs text-slate-500 whitespace-nowrap">
                      {formatDateOnly(dept.created_at, settings.timezone)}
                    </td>
                    <td className="px-6 py-4 text-right whitespace-nowrap">
                      <div className="inline-flex items-center gap-1">
                        {can('departments.edit') && (
                          <>
                            <button
                              type="button"
                              onClick={() => handleToggleStatus(dept)}
                              title={dept.status === 'active' ? 'Deactivate department' : 'Activate department'}
                              className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded transition"
                            >
                              <Power className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleOpenEdit(dept)}
                              title="Edit department"
                              className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded transition"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                        {can('departments.delete') && (
                          <button
                            type="button"
                            onClick={() =>
                              setConfirmDialog({ open: true, dept, loading: false })
                            }
                            title="Delete or safely deactivate"
                            className="p-1.5 text-rose-600 hover:bg-rose-50 rounded transition"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create / Edit Department Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingDept ? 'Edit Department' : 'Create New Department'}
        description="Departments organize company personnel and enforce data isolation."
        maxWidth="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            label="Department Name"
            required
            value={formData.name}
            onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
            placeholder="e.g. Sales, Technical, Operations"
          />

          <Input
            label="Department Code"
            required
            value={formData.code}
            onChange={e => setFormData(prev => ({ ...prev, code: e.target.value.toUpperCase() }))}
            placeholder="e.g. SALES, TECH, FIN"
            helperText="Short uppercase unique identifier"
          />

          <div className="w-full">
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-700 mb-1">
              Description
            </label>
            <textarea
              rows={3}
              value={formData.description}
              onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Outline department duties and operational scope..."
              className="block w-full rounded-lg border border-slate-300 text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-600 bg-white"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Select
              label="Department Manager"
              value={formData.manager_user_id}
              onChange={e => setFormData(prev => ({ ...prev, manager_user_id: e.target.value }))}
              options={[
                { value: '', label: 'Select Manager (Optional)' },
                ...users.map(u => ({ value: u.id, label: `${u.full_name} (${u.email})` })),
              ]}
            />

            <Select
              label="Status"
              value={formData.status}
              onChange={e => setFormData(prev => ({ ...prev, status: e.target.value }))}
              options={[
                { value: 'active', label: 'Active' },
                { value: 'inactive', label: 'Inactive' },
              ]}
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
            <Button variant="outline" type="button" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" loading={formSubmitting}>
              {editingDept ? 'Save Changes' : 'Create Department'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Safe Deletion / Deactivation Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, dept: null, loading: false })}
        onConfirm={handleConfirmDelete}
        title="Confirm Department Deletion / Deactivation"
        message={
          confirmDialog.dept && ((confirmDialog.dept.user_count || 0) > 0 || (confirmDialog.dept.record_count || 0) > 0)
            ? `Notice: "${confirmDialog.dept.name}" contains ${confirmDialog.dept.user_count || 0} user(s) and ${confirmDialog.dept.record_count || 0} business record(s). To protect historical audit trails and data integrity, this department will be safely deactivated rather than permanently erased.`
            : `Are you sure you want to delete the department "${confirmDialog.dept?.name}"? This action cannot be undone.`
        }
        confirmLabel={
          confirmDialog.dept && ((confirmDialog.dept.user_count || 0) > 0 || (confirmDialog.dept.record_count || 0) > 0)
            ? 'Safely Deactivate'
            : 'Delete Department'
        }
        loading={confirmDialog.loading}
      />
    </div>
  );
};
